// simple-autocomplete-light.component.ts
/**
 * Autocomplete component with support for custom validators
 */
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { EnfafIconComponent } from '../enfaf-icon/enfaf-icon.component';

export interface SelectOption {
  id: string;
  title: string;
  subtitle?: string;
  meta?: any;
}

@Component({
  // Legacy tag used by several templates; renaming it is out of the scope of this fix.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'simple-autocomplete-light',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatIconModule, MatButtonModule, EnfafIconComponent],
  templateUrl: './simple-autocomplete-light.component.html',
  styleUrl: './simple-autocomplete-light.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SimpleAutocompleteLightComponent),
      multi: true
    }
  ]
})
export class SimpleAutocompleteLightComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;
  @ViewChild('input', { read: ElementRef }) inputRef!: ElementRef<HTMLInputElement>;

  @Input() label: string = '';
  @Input() placeholder: string = 'Buscar elemento';
  @Input() errorLabel: string = '';
  @Input() pageSize: number = 10;
  @Input() minSearchLength: number = 0;
  @Input() fetchData!: (search: string, page: number, pageSize: number) => Observable<{ data: SelectOption[]; total: number }>;
  @Input() hiddenBoxErrors: boolean = false;
  @Input() disabled: boolean = false;
  // Options to disable in the autocomplete
  @Input() disableOptions: SelectOption[] = [];

  @Input() set control(formControl: FormControl | null) {
    if (formControl) {
      this._externalControl = formControl;
      this.searchControl = formControl;
      this.setupControlSync();
      this.loadOptions(this.searchControl.value || '', 0).subscribe();
    }
  }

  /**
   * Selection given by the parent. Writing it never emits `selectedChanged` nor calls `onChange`: those outputs
   * report what the USER did (pick an option, clear it, type a new value). Emitting here echoed the parent's own
   * state back to it — e.g. the `null` of the first binding cleared a value the parent had just preset
   * (ENF-2372 D-B2). Callers that reset the field (empty option) already reset their own state.
   */
  @Input() set selectedOption(option: SelectOption | null) {
    if (option && option.id !== '') {
      this.selected = option;
      this.searchControl.setValue(option.title, { emitEvent: false });
    } else {
      this.selected = null;
      // Still clears the visible text (and reloads the unfiltered options) when there was one.
      if (this.searchControl.value) {
        this.searchControl.setValue('');
      }
    }
  }
  @Input() required: boolean = false;

  @Output() selectedChanged = new EventEmitter<SelectOption | null>();

  private _externalControl: FormControl | null = null;

  private globalScrollListener: any;

  searchControl = new FormControl('');
  selected: SelectOption | null = null;
  options: SelectOption[] = [];
  isOpen = false;
  loading = false;
  page = 0;
  total = 0;
  onChange: any = () => {};
  onTouched: any = () => {};

  get filteredOptions(): SelectOption[] {
    return this.options.filter((option) => this.disableOptions.every((disabled) => disabled.id !== option.id));
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && !changes['disabled'].firstChange) {
      if (this.disabled) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    }
  }

  ngOnInit() {
    // Apply initial disabled state
    if (this.disabled) {
      this.searchControl.disable();
    }

    if (!this._externalControl) {
      this.setupControlSync();
    }
  }

  private setupControlSync(): void {
    this.searchControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value) => {
          if (typeof value === 'string') {
            this.page = 0;
            return this.loadOptions(value || '', 0);
          }
          return of([]);
        })
      )
      .subscribe((loadedOptions) => {
        this.checkExactMatch();
      });
  }

  /**
   * Check if the current input value exactly matches an option
   * If it does, select it automatically
   * If it doesn't and has value, emit as new option
   */
  private checkExactMatch(): void {
    const currentValue = this.searchControl.value;

    if (!currentValue || typeof currentValue !== 'string') {
      return;
    }

    const trimmedValue = currentValue.trim();
    if (!trimmedValue) {
      return;
    }

    // Titles are compared trimmed too: a stored name like "Product " must still match its own option,
    // or the pick would be replaced by a "new" option when the field loses focus.
    const sameTitle = (title: string) => title.trim().toLowerCase() === trimmedValue.toLowerCase();

    if (this.selected && sameTitle(this.selected.title)) {
      return;
    }

    const exactMatch = this.options.find((option) => sameTitle(option.title));

    if (exactMatch) {
      this.selectOption(exactMatch);
    } else {
      if (!this.isOpen) {
        this.selectNewOption(trimmedValue);
      }
    }
  }

  /**
   * Select a new option that doesn't exist in the list
   */
  private selectNewOption(value: string): void {
    const newOption: SelectOption = {
      id: 'new',
      title: value
    };

    this.selected = newOption;
    this.onChange(newOption.id);
    this.selectedChanged.emit(newOption);
  }

  loadOptions(search: string, page: number) {
    this.loading = true;
    return this.fetchData(search, page, this.pageSize).pipe(
      switchMap((resp: { data: SelectOption[]; total: number }) => {
        this.loading = false;
        this.total = resp.total;
        if (page === 0) {
          this.options = resp.data;
        } else {
          this.options = [...this.options, ...resp.data];
        }
        // The parent may use OnPush: an HTTP answer is not an event of this view, so ask Angular to repaint it.
        this.cdr.markForCheck();

        return of(resp.data);
      })
    );
  }

  loadMore() {
    this.page++;
    this.loadOptions(this.searchControl.value || '', this.page).subscribe();
  }

  onLoadMoreClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    const panel = document.querySelector('.mat-mdc-autocomplete-panel') as HTMLElement;
    const savedScrollTop = panel ? panel.scrollTop : 0;

    this.page++;
    this.loadOptions(this.searchControl.value || '', this.page).subscribe(() => {
      setTimeout(() => {
        const panelAfterLoad = document.querySelector('.mat-mdc-autocomplete-panel') as HTMLElement;
        if (panelAfterLoad) {
          panelAfterLoad.scrollTop = savedScrollTop;
        }
      }, 50);
    });
  }

  // Event handlers for panel state management
  onInputFocus() {
    if (!this.isOpen) {
      this.autocomplete.openPanel();
    }
  }

  onInputClick() {
    if (!this.isOpen) {
      this.autocomplete.openPanel();
    }
  }

  onInputBlur() {
    setTimeout(() => {
      if (!this.isOpen) {
        this.checkExactMatch();
      }
    }, 200);
  }

  onEnterPress(event: Event) {
    event.preventDefault();

    if (this.isOpen && this.autocomplete) {
      this.autocomplete.closePanel();
    }

    setTimeout(() => {
      this.checkExactMatch();
    }, 100);
  }

  onChevronClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      this.autocomplete.closePanel();
    } else {
      this.autocomplete.openPanel();
    }
  }

  onPanelOpened() {
    this.isOpen = true;
    this.globalScrollListener = () => {
      if (this.autocomplete && this.autocomplete.panelOpen) {
        this.autocomplete.updatePosition();
      }
    };
    window.addEventListener('scroll', this.globalScrollListener, true);
  }

  onPanelClosed() {
    this.isOpen = false;
    if (this.globalScrollListener) {
      window.removeEventListener('scroll', this.globalScrollListener, true);
      this.globalScrollListener = null;
    }

    setTimeout(() => {
      this.checkExactMatch();
    }, 100);
  }

  ngOnDestroy() {
    if (this.globalScrollListener) {
      window.removeEventListener('scroll', this.globalScrollListener, true);
      this.globalScrollListener = null;
    }
  }

  selectOption(option: SelectOption) {
    this.selected = option;
    this.searchControl.setValue(option.title, { emitEvent: false });
    this.onChange(option.id);
    this.selectedChanged.emit(option);

    if (this.autocomplete) {
      this.autocomplete.closePanel();
    }
  }

  writeValue(val: any): void {
    if (val) {
      if (typeof val === 'string') {
        const option = this.options.find((o) => o.id === val);
        if (option) {
          this.selected = option;
          this.searchControl.setValue(option.title, { emitEvent: false });
        }
      } else if (typeof val === 'object' && val.id) {
        this.selected = val;
        this.searchControl.setValue(val.title, { emitEvent: false });
      }
    } else {
      this.selected = null;
      this.searchControl.setValue('', { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  clearSelection() {
    this.selected = null;
    this.searchControl.setValue('', { emitEvent: true });
    this.onChange(null);
    this.selectedChanged.emit(null);
  }

  isOptionSelected(option: SelectOption): boolean {
    return this.selected?.id === option.id;
  }

  // TrackBy function para mejorar performance
  trackByFn(index: number, item: SelectOption): string {
    return item.id;
  }

  resetSelection() {
    this.selected = null;
    this.options = [];
    this.page = 0;
    this.total = 0;
    this.searchControl.setValue('', { emitEvent: false });
    this.onChange(null);

    if (this.isOpen && this.autocomplete) {
      this.autocomplete.closePanel();
    }
  }
}
